import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import TabToggle from '@/shared/components/TabToggle/TabToggle';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import multiHumanEmptyScreenAnimation from '../../../../../public/assets/lottie/multiHumanEmptyScreenAnimation.json';
import FormPreview from '../createForm/FormPreview';
import { useViewForm } from './useViewForm';
import { TASK_FORM_LABELS } from '../../constants/labels';
import { TASK_FORM_MANAGEMENT_LABELS as TL } from '../../constants/translations/labels';

const EDIT_PATH = (id: string) => `/Formularze-zadań/Edytuj-Formularz/${id}`;

type ViewFormTab = 'details' | 'technicians';

const TABS = [
  { id: 'details' as ViewFormTab, label: TASK_FORM_LABELS.VIEW_TAB_DETAILS },
  {
    id: 'technicians' as ViewFormTab,
    label: TL.VIEW_TAB_TECHNICIANS.polish,
  },
];

export default function ViewForm({ id }: { id: string }) {
  const navigate = useNavigate();
  const { loading, form, formBuilderState, notFound, error } = useViewForm(id);
  const [activeTab, setActiveTab] = useState<ViewFormTab>('details');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (error || !form || !formBuilderState) {
    return (
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4">
        <p className="text-sm text-[#E11B1B]">
          {error || TASK_FORM_LABELS.VIEW_ERROR_GENERIC}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="view-form-container"
      className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden"
    >
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between gap-4">
        <h1 className="text-[22px] font-normal leading-7 text-[#181A1B] min-w-0 truncate">
          {TASK_FORM_LABELS.VIEW_PAGE_TITLE}
        </h1>
        <button
          type="button"
          data-testid="view-form-edit-button"
          onClick={() => navigate(EDIT_PATH(id))}
          className="shrink-0 h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer"
        >
          <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
            {TL.EDIT_TITLE.polish}
          </span>
        </button>
      </div>

      {/* Tab toggle */}
      <div className="shrink-0">
        <TabToggle
          tabs={TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
          ariaLabel="Zakładki widoku formularza"
        />
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {activeTab === 'details' ? (
          <div className="flex flex-col gap-4 pb-4">
            <span
              data-testid="view-form-id"
              className="text-titleDefault font-medium text-[#474A4C]"
            >
              {form.form_id}
            </span>

            {/* Form preview */}
            <FormPreview
              formState={formBuilderState}
              isSubmitting
              showBanner={false}
            />
          </div>
        ) : (
          <div className="w-full h-full rounded-lg border border-[#DCDDE0] bg-white flex flex-col">
            <div className="px-4 py-3">
              <span className="text-titleDefault font-medium text-[#474A4C]">
                {TL.VIEW_TAB_TECHNICIANS.polish}
              </span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <LottieAnimation
                animationData={multiHumanEmptyScreenAnimation}
                width={277}
                aspectRatio="1 / 1"
              />
              <div className="flex flex-col items-center gap-1">
                <p className="text-base font-medium leading-6 tracking-[0.5px] text-[#474A4C] text-center">
                  {TL.VIEW_TECHNICIANS_EMPTY_TITLE.polish}
                </p>
                <p className="text-base font-normal leading-6 tracking-[0.5px] text-[#474A4C] text-center">
                  {TL.VIEW_TECHNICIANS_EMPTY_SUBTITLE.polish}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
